import React, { useMemo, useState } from 'react';
import { AdminLayout } from 'apps/sigma2-ui/src/components/AdminLayout';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { AdminBreadcrumbs } from 'apps/sigma2-ui/src/components/ui/AdminBreadcrumbs';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { CircularProgress } from '@mui/material';
import { SecondaryButtonLightBg } from 'apps/sigma2-ui/src/components/SecondaryButtonLightBg';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { useSearchParams } from 'react-router-dom';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { Organizations } from 'libs/end-to-end/src/types/organizations/organizations';
import { useConnectionsConfiguration } from 'apps/sigma2-ui/src/lib/hooks/useConnectionsConfiguration';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { ConnectionId } from 'libs/end-to-end/src/connections/connections-config';
import { GreenhouseCredentialsForm } from 'apps/sigma2-ui/src/components/connections/GreenhouseCredentialsForm';
import { LeverCredentialsForm } from 'apps/sigma2-ui/src/components/connections/LeverCredentialsForm';
import { Modal } from 'apps/sigma2-ui/src/components/ui/Modal';
import { useOnChange } from 'apps/sigma2-ui/src/lib/hooks/use-on-change';
import { FountainCredentialsForm } from 'apps/sigma2-ui/src/components/connections/FountainCredentialsForm';
import { AshbyCredentialsForm } from 'apps/sigma2-ui/src/components/connections/AshbyCredentialsForm';

export const ConnectionsPage = () => {
  const language = useLanguage();
  const [searchParams] = useSearchParams();

  const [selectedOrg, setSelectedOrg] = useState<string | null>(
    searchParams.get('organizationId'),
  );

  const [isGreenhouseModalOpen, setIsGreenhouseModalOpen] = useState(false);
  const [wasInstalling, setWasInstalling] = useState(false);
  const [isLeverModalOpen, setIsLeverModalOpen] = useState(false);
  const [wasInstallingLever, setWasInstallingLever] = useState(false);
  const [isFountainModalOpen, setIsFountainModalOpen] = useState(false);
  const [wasInstallingFountain, setWasInstallingFountain] = useState(false);
  const [isAshbyModalOpen, setIsAshbyModalOpen] = useState(false);
  const [wasInstallingAshby, setWasInstallingAshby] = useState(false);

  const errorUrlParam = useMemo(
    () => searchParams.get('error'),
    [searchParams],
  );
  const connectionIdUrlParam = useMemo(() => {
    const raw = searchParams.get('connectionId');
    if (!raw) {
      return null;
    }

    const parsed = ConnectionId.safeParse(raw);
    if (!parsed.success) {
      return null;
    }

    return parsed.data;
  }, [searchParams]);

  const organizationsRead = useSimpleReadPattern({
    getUrlFn: () => `${getApiBaseUrl()}/organizations`,
    canRead: true,
    responseSchema: Organizations,
  });

  const selectableOrganizations = useMemo(() => {
    if (organizationsRead.status !== 'done') return [];

    return Object.values(organizationsRead.entity.organizations).map((org) => ({
      optionValue: org.id,
      optionLabel: org.name,
    }));
  }, [organizationsRead]);

  const connections = useConnectionsConfiguration(selectedOrg ?? undefined);

  const {
    state,
    config,
    errorMessage,
    installConnection,
    installGreenhouseConnection,
    testGreenhouseConnection,
    installLeverConnection,
    installFountainConnection,
    testFountainConnection,
    installAshbyConnection,
    testAshbyConnection,
    uninstallConnection,
  } = connections;

  const noConnectionsEnabled = useMemo(() => {
    if (state !== 'succeeded' || !config || !config.isEnabled) {
      return false;
    }

    return Object.values(config.connections).every((c) => !c.isEnabled);
  }, [config, state]);

  const closeModalWhenInstallationSucceedsState = useMemo(() => {
    return {
      wasInstalling: wasInstalling,
      state: state,
      config: config,
    };
  }, [wasInstalling, state, config]);

  useOnChange(closeModalWhenInstallationSucceedsState, ({ current }) => {
    if (
      current.wasInstalling &&
      current.state === 'succeeded' &&
      current.config?.isEnabled &&
      current.config?.connections.greenhouse.isEnabled &&
      current.config?.connections.greenhouse.installation.isInstalled
    ) {
      setIsGreenhouseModalOpen(false);
      setWasInstalling(false);
    }
  });

  const closeLeverModalWhenInstallationSucceedsState = useMemo(() => {
    return {
      wasInstallingLever: wasInstallingLever,
      state: state,
      config: config,
    };
  }, [wasInstallingLever, state, config]);

  useOnChange(closeLeverModalWhenInstallationSucceedsState, ({ current }) => {
    if (
      current.wasInstallingLever &&
      current.state === 'succeeded' &&
      current.config?.isEnabled &&
      current.config?.connections.lever.isEnabled &&
      current.config?.connections.lever.installation.isInstalled
    ) {
      setIsLeverModalOpen(false);
      setWasInstallingLever(false);
    }
  });

  const closeFountainModalWhenInstallationSucceedsState = useMemo(() => {
    return {
      wasInstallingFountain: wasInstallingFountain,
      state: state,
      config: config,
    };
  }, [wasInstallingFountain, state, config]);

  useOnChange(
    closeFountainModalWhenInstallationSucceedsState,
    ({ current }) => {
      if (
        current.wasInstallingFountain &&
        current.state === 'succeeded' &&
        current.config?.isEnabled &&
        current.config?.connections.fountain.isEnabled &&
        current.config?.connections.fountain.installation.isInstalled
      ) {
        setIsFountainModalOpen(false);
        setWasInstallingFountain(false);
      }
    },
  );

  const closeAshbyModalWhenInstallationSucceedsState = useMemo(() => {
    return {
      wasInstallingAshby: wasInstallingAshby,
      state: state,
      config: config,
    };
  }, [wasInstallingAshby, state, config]);

  useOnChange(closeAshbyModalWhenInstallationSucceedsState, ({ current }) => {
    if (
      current.wasInstallingAshby &&
      current.state === 'succeeded' &&
      current.config?.isEnabled &&
      current.config?.connections.ashby.isEnabled &&
      current.config?.connections.ashby.installation.isInstalled
    ) {
      setIsAshbyModalOpen(false);
      setWasInstallingAshby(false);
    }
  });

  return (
    <AdminLayout currentPage="Connections">
      <AdminBreadcrumbs
        items={[
          { label: 'Home', to: '/admin' },
          { label: language.connectionsPageTitle(), to: '/connections' },
        ]}
      />

      <Box as={'div'} display={'flex'} alignItems={'center'}>
        <Spacing size="md" direction="vertical">
          <S2Typography
            variant="headline-4"
            translatedContent={language.connectionsPageTitle()}
          />

          {organizationsRead.status === 'loading' && <CircularProgress />}

          {organizationsRead.status === 'error' && (
            <S2Typography
              variant="body-6"
              textColorVariant="red-500"
              translatedContent={language.unknownError()}
            />
          )}

          {errorUrlParam && connectionIdUrlParam && (
            <S2Typography
              variant="body-6"
              textColorVariant="red-500"
              translatedContent={language.connectionsInstallError({
                connectionId: connectionIdUrlParam,
                error: errorUrlParam,
              })}
            />
          )}

          {organizationsRead.status === 'done' && (
            <>
              <hr className="border-0 border-t border-[#E7E8E8] my-1" />
              <SelectBoxWithSearch
                type="single"
                label="Select Organization"
                list={selectableOrganizations}
                selected={selectedOrg ?? ''}
                setSelected={setSelectedOrg}
              />
            </>
          )}

          {selectedOrg && state === 'loading' && <CircularProgress />}

          {state === 'failed' && (
            <S2Typography
              variant="body-6"
              textColorVariant="red-500"
              translatedContent={errorMessage ?? language.unknownError()}
            />
          )}

          {state === 'succeeded' && config && (
            <>
              {!config.isEnabled && (
                <S2Typography
                  variant="body-6"
                  translatedContent={language.noConnectionsEnabled()}
                />
              )}

              {noConnectionsEnabled && (
                <S2Typography
                  variant="body-6"
                  translatedContent={language.noConnectionsEnabled()}
                />
              )}

              {config.isEnabled &&
                config.connections.slack.isEnabled &&
                !noConnectionsEnabled && (
                  <Spacing size="gap-1" direction="vertical">
                    <S2Typography
                      variant="headline-5"
                      translatedContent={language.connectionsPageSlackHeading()}
                    />

                    <S2Typography
                      variant="body-6"
                      translatedContent={language.connectionsPageSlackDescription()}
                    />

                    {!config.connections.slack.installation.isInstalled && (
                      <a
                        className="w-fit cursor-pointer"
                        onClick={() => {
                          installConnection('slack');
                        }}
                      >
                        <img
                          alt="Add to Slack"
                          height="40"
                          width="139"
                          src="https://platform.slack-edge.com/img/add_to_slack.png"
                          srcSet="
                        https://platform.slack-edge.com/img/add_to_slack.png 1x,
                        https://platform.slack-edge.com/img/add_to_slack@2x.png 2x
                      "
                        />
                      </a>
                    )}

                    {config.connections.slack.installation.isInstalled && (
                      <SecondaryButtonLightBg
                        className="w-fit"
                        translatedContent={language.connectionsPageSlackDisconnect()}
                        onClick={() => {
                          void uninstallConnection('slack');
                        }}
                        disabled={false}
                      />
                    )}

                    <hr className="border-0 border-t border-[#E7E8E8] my-1" />
                  </Spacing>
                )}

              {config.isEnabled &&
                config.connections.greenhouse.isEnabled &&
                !noConnectionsEnabled && (
                  <Spacing size="gap-1" direction="vertical">
                    <S2Typography
                      variant="headline-5"
                      translatedContent="Greenhouse"
                    />

                    <S2Typography
                      variant="body-6"
                      translatedContent="Connect your Greenhouse account to enable integrations"
                    />

                    {!config.connections.greenhouse.installation
                      .isInstalled && (
                      <button
                        onClick={() => {
                          setIsGreenhouseModalOpen(true);
                        }}
                        className="border border-gray-300 rounded-lg p-4 hover:border-gray-400 transition-colors bg-white w-fit"
                        data-qa="greenhouse-connect-button"
                      >
                        <img
                          src="https://cdn.bfldr.com/VEU5CHFS/at/j9fvq5bhbqhcsvwckq93nkp/GREENHOUSE_WORDMARK_GREEN.png?auto=webp&format=png"
                          alt="Greenhouse"
                          className="h-4"
                        />
                      </button>
                    )}

                    {config.connections.greenhouse.installation.isInstalled && (
                      <SecondaryButtonLightBg
                        className="w-fit"
                        translatedContent="Disconnect Greenhouse"
                        onClick={() => {
                          void uninstallConnection('greenhouse');
                        }}
                        disabled={false}
                      />
                    )}

                    <hr className="border-0 border-t border-[#E7E8E8] my-1" />
                  </Spacing>
                )}

              {config.isEnabled &&
                config.connections.lever.isEnabled &&
                !noConnectionsEnabled && (
                  <Spacing size="gap-1" direction="vertical">
                    <S2Typography
                      variant="headline-5"
                      translatedContent="Lever"
                    />

                    <S2Typography
                      variant="body-6"
                      translatedContent="Connect your Lever account to enable integrations"
                    />

                    {!config.connections.lever.installation.isInstalled && (
                      <button
                        onClick={() => {
                          setIsLeverModalOpen(true);
                        }}
                        className="border border-gray-300 rounded-lg p-4 hover:border-gray-400 transition-colors bg-white w-fit"
                        data-qa="lever-connect-button"
                      >
                        Connect Lever
                      </button>
                    )}

                    {config.connections.lever.installation.isInstalled && (
                      <SecondaryButtonLightBg
                        className="w-fit"
                        translatedContent="Disconnect Lever"
                        onClick={() => {
                          void uninstallConnection('lever');
                        }}
                        disabled={false}
                      />
                    )}

                    <hr className="border-0 border-t border-[#E7E8E8] my-1" />
                  </Spacing>
                )}

              {config.isEnabled &&
                config.connections.fountain.isEnabled &&
                !noConnectionsEnabled && (
                  <Spacing size="gap-1" direction="vertical">
                    <S2Typography
                      variant="headline-5"
                      translatedContent="Fountain"
                    />

                    <S2Typography
                      variant="body-6"
                      translatedContent="Connect your Fountain account to enable integrations"
                    />

                    {!config.connections.fountain.installation.isInstalled && (
                      <button
                        onClick={() => {
                          setIsFountainModalOpen(true);
                        }}
                        className="border border-gray-300 rounded-lg p-4 hover:border-gray-400 transition-colors bg-white w-fit"
                        data-qa="fountain-connect-button"
                        type="button"
                      >
                        Connect Fountain
                      </button>
                    )}

                    {config.connections.fountain.installation.isInstalled && (
                      <SecondaryButtonLightBg
                        className="w-fit"
                        translatedContent="Disconnect Fountain"
                        onClick={() => {
                          void uninstallConnection('fountain');
                        }}
                        disabled={false}
                      />
                    )}

                    <hr className="border-0 border-t border-[#E7E8E8] my-1" />
                  </Spacing>
                )}

              {config.isEnabled &&
                config.connections.ashby.isEnabled &&
                !noConnectionsEnabled && (
                  <Spacing size="gap-1" direction="vertical">
                    <S2Typography
                      variant="headline-5"
                      translatedContent="Ashby"
                    />

                    <S2Typography
                      variant="body-6"
                      translatedContent="Connect your Ashby account to enable integrations"
                    />

                    {!config.connections.ashby.installation.isInstalled && (
                      <button
                        onClick={() => {
                          setIsAshbyModalOpen(true);
                        }}
                        className="border border-gray-300 rounded-lg p-4 hover:border-gray-400 transition-colors bg-white w-fit"
                        data-qa="ashby-connect-button"
                        type="button"
                      >
                        <img
                          src="https://cdn.brandfetch.io/idomC5_5qC/theme/dark/logo.svg?c=1bxid64Mup7aczewSAYMX&t=1721803768335"
                          alt="Ashby"
                          className="h-4"
                        />
                      </button>
                    )}

                    {config.connections.ashby.installation.isInstalled && (
                      <SecondaryButtonLightBg
                        className="w-fit"
                        translatedContent="Disconnect Ashby"
                        onClick={() => {
                          void uninstallConnection('ashby');
                        }}
                        disabled={false}
                      />
                    )}

                    <hr className="border-0 border-t border-[#E7E8E8] my-1" />
                  </Spacing>
                )}

              {/* Greenhouse Connection Modal */}
              <Modal
                show={isGreenhouseModalOpen}
                onClose={() => {
                  setIsGreenhouseModalOpen(false);
                }}
                title="Connect Greenhouse"
              >
                <GreenhouseCredentialsForm
                  onSubmit={(clientId, clientSecret) => {
                    setWasInstalling(true);
                    void installGreenhouseConnection(clientId, clientSecret);
                  }}
                  onTest={(clientId, clientSecret) =>
                    testGreenhouseConnection(clientId, clientSecret)
                  }
                  isLoading={wasInstalling}
                  error={errorMessage ?? null}
                />
              </Modal>

              {/* Lever Connection Modal */}
              <Modal
                show={isLeverModalOpen}
                onClose={() => {
                  setIsLeverModalOpen(false);
                }}
                title="Connect Lever"
              >
                <LeverCredentialsForm
                  onSubmit={(apiKey) => {
                    setWasInstallingLever(true);
                    void installLeverConnection(apiKey);
                  }}
                  isLoading={wasInstallingLever}
                  error={errorMessage ?? null}
                />
              </Modal>

              {/* Fountain Connection Modal */}
              <Modal
                show={isFountainModalOpen}
                onClose={() => {
                  setIsFountainModalOpen(false);
                }}
                title="Connect Fountain"
              >
                <FountainCredentialsForm
                  hireApiBaseUrl={
                    state === 'succeeded' &&
                    config.isEnabled &&
                    config.connections.fountain.isEnabled
                      ? config.connections.fountain.hireApiBaseUrl
                      : undefined
                  }
                  onSubmit={(payload) => {
                    setWasInstallingFountain(true);
                    void installFountainConnection(payload);
                  }}
                  onTest={(payload) => testFountainConnection(payload)}
                  isLoading={wasInstallingFountain}
                  error={errorMessage ?? null}
                />
              </Modal>

              {/* Ashby Connection Modal */}
              <Modal
                show={isAshbyModalOpen}
                onClose={() => {
                  setIsAshbyModalOpen(false);
                }}
                title="Connect Ashby"
              >
                <AshbyCredentialsForm
                  onSubmit={(apiKey) => {
                    setWasInstallingAshby(true);
                    void installAshbyConnection(apiKey);
                  }}
                  onTest={(apiKey) => testAshbyConnection(apiKey)}
                  isLoading={wasInstallingAshby}
                  error={errorMessage ?? null}
                />
              </Modal>
            </>
          )}
        </Spacing>
      </Box>
    </AdminLayout>
  );
};
